import * as apiValues from '../constants/apiValues';
import PropTypes from 'prop-types';
import React from 'react';
import {Carousel} from 'react-responsive-carousel';
import {applyBalancedDotRows} from '../utils/balanceDotRows';
import {
  createProjectPhotosWrapSnapper,
  projectPhotosAnimationHandler,
  projectPhotosStopSwipingHandler,
  projectPhotosSwipeAnimationHandler,
} from '../utils/projectPhotosCarousel';
import MarkdownField from './MarkdownField';
import BodyEnd from './BodyEnd';

export const LIGHTBOX_SCROLL_LOCK_CLASS = 'lightbox-scroll-lock';


export default class Lightbox extends React.Component{
  closeButtonRef = React.createRef();
  carouselContainerRef = React.createRef();
  carouselRef = React.createRef();
  wrapSnapper = createProjectPhotosWrapSnapper((itemListStyle) => {
    this.applyWrapSnap(itemListStyle);
  });
  applyWrapSnap = (itemListStyle) => {
    const list = this.carouselContainerRef.current && this.carouselContainerRef.current.querySelector('.slider');
    if(list){
      [
        'WebkitTransform',
        'msTransform',
        'OTransform',
        'transform',
        'WebkitTransitionDuration',
        'MozTransitionDuration',
        'OTransitionDuration',
        'msTransitionDuration',
        'transitionDuration',
      ].forEach((prop) => {
        if(itemListStyle[prop] != null) list.style[prop] = itemListStyle[prop];
      });
      void list.offsetWidth;
    }
    if(this.carouselRef.current){
      this.carouselRef.current.setState({itemListStyle});
    }
  };
  animationHandler = (props, state) => (
    projectPhotosAnimationHandler(props, state, 'start', this.wrapSnapper)
  );
  swipeAnimationHandler = (delta, props, state, setState) => (
    projectPhotosSwipeAnimationHandler(delta, props, state, setState, 'start', this.wrapSnapper)
  );
  stopSwipingHandler = (props, state) => (
    projectPhotosStopSwipingHandler(props, state, 'start', this.wrapSnapper)
  );

  componentDidMount(){
    document.addEventListener('keydown', this.document_onKeyDown);
    window.addEventListener('resize', this.balanceDots);
    if(this.props.isOpen){
      this.lockBackground();
    }
    this.balanceDots();
  }
  getSnapshotBeforeUpdate(prevProps){
    // Unlock while the overlay still covers the page so removing position:fixed
    // cannot paint the document at y=0.
    if(prevProps.isOpen && !this.props.isOpen){
      if(this.closeButtonRef.current){
        this.closeButtonRef.current.blur();
      }
      this.unlockBackground();
    }
    return null;
  }
  componentDidUpdate(prevProps){
    if(!prevProps.isOpen && this.props.isOpen){
      this.lockBackground();
    }
    if(this.props.isOpen){
      this.balanceDots();
    }
  }
  componentWillUnmount(){
    document.removeEventListener('keydown', this.document_onKeyDown);
    window.removeEventListener('resize', this.balanceDots);
    if(this._dotsFrame) window.cancelAnimationFrame(this._dotsFrame);
    this.wrapSnapper.cancel();
    this.unlockBackground();
  }
  balanceDots = () => {
    if(this._dotsFrame) window.cancelAnimationFrame(this._dotsFrame);
    this._dotsFrame = window.requestAnimationFrame(() => {
      this._dotsFrame = null;
      const dots = this.carouselContainerRef.current && this.carouselContainerRef.current.querySelector('.control-dots');
      applyBalancedDotRows(dots);
    });
  };


  closeButton_onClick = () => {
    //console.log(`MListMedia.closeButton_onClick`, this);
    this.props.onCloseClick();
  };
  document_onKeyDown = (event) => {
    if(!this.props.isOpen || event.key !== 'Escape') return;
    event.preventDefault();
    this.props.onCloseClick();
  };
  overlay_onClick = (event) => {
    if(event.target === event.currentTarget){
      this.props.onCloseClick();
    }
  };
  setBackgroundInert = (inert) => {
    const app = document.getElementById('app');
    if(!app) return;
    if(inert) app.setAttribute('inert', '');
    else app.removeAttribute('inert');
  };
  restoreWindowScroll = (scrollY) => {
    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    if(document.scrollingElement){
      document.scrollingElement.scrollTop = scrollY;
    }
    root.scrollTop = scrollY;
    if(document.body){
      document.body.scrollTop = scrollY;
    }
    if(typeof window.scrollTo === 'function'){
      try{
        window.scrollTo({top: scrollY, left: 0, behavior: 'instant'});
      }
      catch(err){
        window.scrollTo(0, scrollY);
      }
    }
    root.style.scrollBehavior = previousBehavior;
  };
  lockBackground = () => {
    this.setBackgroundScrollLock(true);
    this.setBackgroundInert(true);
    if(this.closeButtonRef.current){
      this.closeButtonRef.current.focus({preventScroll: true});
    }
  };
  unlockBackground = () => {
    this.setBackgroundScrollLock(false);
    this.setBackgroundInert(false);
  };
  setBackgroundScrollLock = (lock) => {
    if(typeof document === 'undefined') return;
    const root = document.documentElement;
    if(lock){
      if(this._scrollLocked) return;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      this._savedScrollY = scrollY;
      root.style.setProperty('--lightbox-scroll-lock-top', `-${scrollY}px`);
      root.classList.add(LIGHTBOX_SCROLL_LOCK_CLASS);
      this._scrollLocked = true;
      return;
    }
    if(!this._scrollLocked) return;
    const savedY = this._savedScrollY || 0;
    root.classList.remove(LIGHTBOX_SCROLL_LOCK_CLASS);
    root.style.removeProperty('--lightbox-scroll-lock-top');
    this._scrollLocked = false;
    this._savedScrollY = 0;
    this.restoreWindowScroll(savedY);
  };


  captionText = (mediaItem) => {
    if(!mediaItem || mediaItem.caption == null) return '';
    return String(mediaItem.caption).trim();
  };
  renderCaption = (mediaItem) => {
    const caption = this.captionText(mediaItem);
    if(!caption) return null;

    return <p className="lightbox__media-caption">{caption}</p>;
  };
  renderIndicator = (onClickHandler, isSelected, index) => {
    return (
      <li key={index} className={isSelected ? 'dot selected' : 'dot'} role="presentation">
        <button
          type="button"
          className="lightbox__dot"
          onClick={onClickHandler}
          aria-label={`Go to image ${index + 1}`}
          aria-current={isSelected ? 'true' : undefined}
        />
      </li>
    );
  };
  renderCarousel = () => {
    const itemCount = (this.props.mediaItems || []).length;
    const showControls = itemCount > 1;

    return (
      <div className="lightbox__carousel-container" ref={this.carouselContainerRef}>
        <Carousel
          animationHandler={this.animationHandler}
          infiniteLoop={showControls}
          labels={{
            leftArrow: 'Previous image',
            rightArrow: 'Next image',
            item: 'Go to image',
          }}
          onSwipeStart={this.wrapSnapper.flush}
          ref={this.carouselRef}
          renderIndicator={this.renderIndicator}
          selectedItem={this.props.selectedItem}
          showArrows={showControls}
          showIndicators={showControls}
          showStatus={false}
          showThumbs={false}
          stopSwipingHandler={this.stopSwipingHandler}
          swipeAnimationHandler={this.swipeAnimationHandler}
        >
          {this.renderSlides()}
        </Carousel>
      </div>
    );
  };
  renderSlides = () => {
    if(!this.props.mediaItems) return null;

    return this.props.mediaItems.map(mediaItem => {
      let mediaSpacer = <div className="lightbox__media-spacer"></div>;

      if(mediaItem.mediaEmbed){
        return (
          <div className="lightbox__media-slide" key={mediaItem.id}>
            <div className="lightbox__media-slide-imagery">
              <MarkdownField className="lightbox__media-imagery-container" markup={mediaItem.mediaEmbed}/>
              {mediaSpacer}
            </div>
            {this.renderCaption(mediaItem)}
          </div>
        );
      }
      else if(mediaItem.mediaVideo){
        let mediaImage = mediaItem.mediaImage ?
                         <div className="lightbox__media-item lightbox__media-video-image" key={mediaItem.id} style={{backgroundImage: `url(${apiValues.imgPath(mediaItem.mediaImage.url)})`}}/> : '';

        return (
          <div className="lightbox__media-slide" key={mediaItem.id}>
            <div className="lightbox__media-slide-imagery">
              <div className="lightbox__media-imagery-container">
                <video className="lightbox__media-imagery"
                       src={apiValues.imgPath(mediaItem.mediaVideo.url)}
                       type="*"
                       autoPlay muted playsInline controls="true"></video>
                {mediaImage}
              </div>
              {mediaSpacer}
            </div>
            {this.renderCaption(mediaItem)}
          </div>
        );
      }
      else if(mediaItem.mediaImage) return (
        <div className="lightbox__media-slide lightbox__media-slide--photo" key={mediaItem.id}>
          <div className="lightbox__media-slide-imagery">
            <div className="lightbox__media-imagery-container">
              <img alt={mediaItem.alt || ''} className="lightbox__media-imagery" src={apiValues.imgPath(mediaItem.mediaImage.url)}/>
            </div>
          </div>
          {this.renderCaption(mediaItem)}
        </div>
      );
    });
  };
  render(){
    console.log(`Lightbox.render()`, this.props);

    const isOpen = Boolean(this.props.isOpen);
    let isOpenClass = isOpen ? 'lightbox--is-open' : '';

    return (
      <BodyEnd>
        <div
          className={`lightbox ${this.props.className || ''} ${isOpenClass}`}
          role="dialog"
          aria-modal={isOpen ? 'true' : undefined}
          aria-hidden={!isOpen}
          aria-label="Image viewer"
          inert={!isOpen ? '' : undefined}
          onClick={this.overlay_onClick}
        >
          <div className="lightbox__box">
            <button
              ref={this.closeButtonRef}
              type="button"
              className="lightbox__close"
              onClick={this.closeButton_onClick}
            >
              Close
            </button>
            {this.renderCarousel()}
          </div>
        </div>
      </BodyEnd>
    );
  }
}


Lightbox.propTypes = {
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  mediaItems: PropTypes.array,
  onCloseClick: PropTypes.func,
  selectedItem: PropTypes.number,
};
